import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import LoadingDataDiv from "../common/LoadingDataDiv";
import { toastError, toastSuccess } from "../../../helpers/Toast";
import "../../../css/EmployeePortal.css";

const typeLabels = {
  HR_MEETING: "Spotkanie z HR",
  QUESTION_TO_MANAGER: "Pytanie do kierownika",
  ANONYMOUS_REPORT: "Anonimowe zgłoszenie"
};

const statusLabels = {
  NEW: "Nowe",
  IN_PROGRESS: "W trakcie",
  CLOSED: "Zamknięte"
};

const getErrorMessage = error => error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null) || "Operacja nie powiodła się.";
const formatDateTime = value => value ? new Date(value).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }) : "-";

const EmployeeContactRequestsManager = () => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [decision, setDecision] = useState({ status: "IN_PROGRESS", managerReply: "" });
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/employee-contact-requests/manager", { params: statusFilter ? { status: statusFilter } : {} });
      setRequests(response.data || []);
    } catch (error) {
      setRequests([]);
      toastError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [axiosPrivate, statusFilter]);

  const openRequest = request => {
    setSelected(request);
    setDecision({ status: request.status === "NEW" ? "IN_PROGRESS" : request.status, managerReply: request.managerReply || "" });
  };

  const saveDecision = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axiosPrivate.put(`/employee-contact-requests/manager/${selected.id}`, decision);
      toastSuccess("Zgłoszenie zostało zaktualizowane.");
      setSelected(null);
      await loadData();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="layoutSidenav_content" className="employee-portal-page">
      <ToastContainer />
      <div className="employee-portal-header shadow-sm">
        <div>
          <div className="employee-portal-eyebrow">Pracownicy</div>
          <h2>Wiadomości i zgłoszenia</h2>
          <div className="employee-portal-subtitle">Pytania do kierowników, anonimowe zgłoszenia i historyczne prośby o spotkanie z HR.</div>
        </div>
      </div>
      <div className="container-fluid px-4 py-4">
        <div className="employee-portal-card">
          <div className="employee-portal-card-header">
            <h5>Zgłoszenia</h5>
            <div className="d-flex gap-2">
              <Form.Select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
                <option value="">Wszystkie</option>
                <option value="NEW">Nowe</option>
                <option value="IN_PROGRESS">W trakcie</option>
                <option value="CLOSED">Zamknięte</option>
              </Form.Select>
              <Button variant="outline-secondary" onClick={loadData}>Odśwież</Button>
            </div>
          </div>
          {loading ? <LoadingDataDiv /> : requests.length === 0 ? <div className="employee-portal-empty">Brak zgłoszeń.</div> : (
            <div className="employee-portal-list">
              {requests.map(request => (
                <div className="employee-portal-list-item" key={request.id}>
                  <div className="d-flex justify-content-between gap-2">
                    <strong>{request.employeeFullName || "Zgłoszenie anonimowe"} · {typeLabels[request.type] || request.type}</strong>
                    <span>{statusLabels[request.status] || request.status}</span>
                  </div>
                  <small>{request.departmentName || "Brak działu"} · {formatDateTime(request.createdAt)}{request.type === "QUESTION_TO_MANAGER" && request.targetManagerFullName ? ` · Do: ${request.targetManagerFullName}` : ""}</small>
                  {request.preferredDate && <small>Preferowany termin: {request.preferredDate}{request.preferredTime ? ` ${String(request.preferredTime).slice(0, 5)}` : ""}</small>}
                  <span>{request.message}</span>
                  {request.managerReply && <div className="employee-portal-reply"><strong>Odpowiedź:</strong> {request.managerReply}</div>}
                  <div><Button size="sm" onClick={() => openRequest(request)}>Otwórz</Button></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal show={!!selected} onHide={() => setSelected(null)} centered>
        <Modal.Header closeButton><Modal.Title>Obsługa zgłoszenia</Modal.Title></Modal.Header>
        <Modal.Body>
          {selected && <div className="mb-3"><strong>{selected.employeeFullName || "Zgłoszenie anonimowe"}</strong><br />{typeLabels[selected.type]} · {selected.departmentName}{selected.type === "QUESTION_TO_MANAGER" && selected.targetManagerFullName ? <><br />Do: {selected.targetManagerFullName}</> : null}</div>}
          <Form.Group className="mb-3"><Form.Label>Status</Form.Label><Form.Select value={decision.status} onChange={event => setDecision(prev => ({ ...prev, status: event.target.value }))}><option value="NEW">Nowe</option><option value="IN_PROGRESS">W trakcie</option><option value="CLOSED">Zamknięte</option></Form.Select></Form.Group>
          <Form.Group><Form.Label>Odpowiedź dla pracownika</Form.Label><Form.Control as="textarea" rows={5} value={decision.managerReply} onChange={event => setDecision(prev => ({ ...prev, managerReply: event.target.value }))} /></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setSelected(null)}>Anuluj</Button><Button onClick={saveDecision} disabled={saving}>Zapisz</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmployeeContactRequestsManager;
